import moment from "moment";
import _ from "lodash";
import ToastificationContent from "@/components/toastification-content";
import { useToast } from "vue-toastification";
import store from '@/state/store';

const helpers = {
  install(app) {
    const globalProperties = app.config.globalProperties;
    const toast = useToast();

    const toastSuccess = (title, text = '') => {
      toast.success({
        component: ToastificationContent,
        props: { title: title, text: text }
      })
    };
    const toastError = (title, text = '') => {
      toast.error({
        component: ToastificationContent,
        props: { title: title, text: text }
      })
    };
    const toastWarning = (title, text = '') => {
      toast.warning({
        component: ToastificationContent,
        props: { title: title, text: text }
      })
    };
    const toastInfo = (title, text = '') => {
      toast.info({
        component: ToastificationContent,
        props: { title: title, text: text }
      })
    };

    globalProperties.toastSuccess = toastSuccess;
    globalProperties.toastError = toastError;
    globalProperties.toastWarning = toastWarning;
    globalProperties.toastInfo = toastInfo;

    globalProperties.toLocalTime = (datetime, format='YYYY-MM-DD hh:mm A') => {
      let  timezone = store.getters['config/selectedTimezone'];

      // Normalize: if timezone looks like just a number (e.g. "+3" or "3")
      if (/^[+-]?\d+$/.test(timezone)) {
        timezone = parseInt(timezone, 10) * 60; // hours → minutes
      }
      return datetime ? moment.utc(datetime).utcOffset(timezone).format(format) : ''
    };

    globalProperties.toUtcTime = (datetime, format='YYYY-MM-DD HH:mm:ss') => {

      let timezone = store.getters['config/selectedTimezone'];

      // Normalize: if timezone is just hours (e.g. "+3" or "-5")
      if (/^[+-]?\d+$/.test(timezone)) {
        const hours = parseInt(timezone, 10);
        // Convert to "+03:00" or "-05:00"
        timezone = (hours >= 0 ? '+' : '-') + String(Math.abs(hours)).padStart(2, '0') + ':00';
      }

      return datetime ? moment.parseZone(datetime + timezone).utc().format(format) : '';
    };

    globalProperties.toLocalStartTime = (format='YYYY-MM-DDTHH:mm') => {

      let timezone = store.getters['config/selectedTimezone'];

      // Normalize: if timezone looks like just a number (e.g. "+3" or "3")
      if (/^[+-]?\d+$/.test(timezone)) {
        timezone = parseInt(timezone, 10) * 60; // hours → minutes
      }
      return moment.utc().utcOffset(timezone).startOf('day').format(format);
    };

    globalProperties.toLocalEndTime = (format='YYYY-MM-DDTHH:mm') => {
      
      let timezone = store.getters['config/selectedTimezone'];

      // Normalize: if timezone looks like just a number (e.g. "+3" or "3")
      if (/^[+-]?\d+$/.test(timezone)) {
        timezone = parseInt(timezone, 10) * 60; // hours → minutes
      }
      return moment.utc().utcOffset(timezone).endOf('day').format(format);
    };

    const snakelizeObject = (obj) => {
      return _.transform(obj, (acc, value, key, target) => {
        const camelKey = _.isArray(target) ? key : _.snakeCase(key);
        acc[camelKey] = _.isObject(value) ? snakelizeObject(value) : value;
      })
    };
    globalProperties.snakelizeObject = snakelizeObject;

    globalProperties.resetObject = (obj) => {
      Object.keys(obj).forEach( (key) => {
        if (Array.isArray(obj[key])) {
          obj[key] = []
        } else if ( obj[key] !== null && typeof obj[key] == 'object') {
           Object.keys(obj[key]).forEach( (key2) => {
            obj[key][key2] = null;
           });
        } else {
          obj[key] = null;
        }
      });
    };

    const formatAmount = (amount, decimal = 2) => {
      // Remove commas if the input is a string
      const cleanAmount = typeof amount === 'string' ? amount.replace(/,/g, '') : amount;
      const num = parseFloat(cleanAmount);
      if (isNaN(num)) return '0.00';
      return num.toLocaleString('en-US', {
        minimumFractionDigits: decimal,
        maximumFractionDigits: decimal
      });
    };
    globalProperties.formatAmount = formatAmount;
  
    globalProperties.errorHandler = (error) => {
      if (error.response) {
        // Destructure status and data only if error.response is defined
        const { status, data } = error.response;

        switch (status) {
          case 400:
            toastError('Fail', data.message || 'Bad Request');
            break;
          case 401: {
            const message = data.message || 'Access denied';
            toastError('Unauthorized', message);
            break;
          }
          case 422: {
            if (
                data.data &&
                (
                    (Array.isArray(data.data) && data.data.length > 0) ||
                    (!Array.isArray(data.data) && Object.keys(data.data).length > 0)
                )
            ) {
              // Check if validationErrors is an object (Laravel style) or an array
              const formattedErrors = Array.isArray(data.data)
                  ? data.data.join('<br>') // If it's an array, join directly
                  : Object.values(data.data).flat().join('<br>'); // If it's an object, flatten and join

              toastError('Validation Failed', formattedErrors);
            } else {
              const match = data.message.match(/\| Data:\s*(\{.*\})$/);

              if (match) {
                const formattedErrors = Object.values(JSON.parse(match[1]))
                    .flat().join('<br>');

                toastError('Validation Error', formattedErrors);
              } else {
                toastError('Fail', data.message || 'Unprocessable Entity');
              }
            }
            break;
          }
          case 403:
            toastError('Fail', data.message || 'Forbidden');
            break;
          default:
            toastError('Fail', data.message || 'An unknown error occurred');
            break;
        }
      } else if (error.request) {
        // No response received (network or server issue)
        toastError('Fail', 'Unable to connect to the server. Please try again.');
      } else if (error.message) {
        toastError('Fail', error.message);
      } else {
        // Something else caused the error (e.g., misconfigured request)
        toastError('Fail', 'An unexpected error occurred. Please check the console for details.');
      }
    };

    globalProperties.isValidUrl = (string) => {
      try {
        new URL(string);
        return true;
      } catch (_) {
        return false;
      }
    };

    globalProperties.isValidEmail = (email) => {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    };

    globalProperties.toOptions = (obj) => {
      return Object.entries(obj).map(([key, value]) => ({
        value: Number(key),
        text: value
      })).sort((a, b) => b.value - a.value);
    };
  } 
}
export default helpers;
